import React, { useMemo, useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

/* =========================
   SYNERGY & COUNTER VISUALIZATION
   eSports Professional Grade
   ========================= */

// ===== Sample Data Structure =====
const SAMPLE_SYNERGY_DATA = {
  // hero1 -> hero2 -> synergy score (0-100)
};

const SAMPLE_COUNTER_DATA = {
  // hero1 -> hero2 -> counter score (-100 to 100, positive = hero1 counters hero2)
};

// ===== Synergy/Counter Calculation =====
export function analyzeHeroRelations({
  hero,
  teamPicks = [],
  enemyPicks = [],
  synergyData = null,
  counterData = null,
  allHeroes = [],
}) {
  const result = {
    synergies: [],      // Heroes that work well with this hero
    counters: [],       // Heroes this hero counters
    counteredBy: [],    // Heroes that counter this hero
    currentTeamSynergy: 0,
    currentMatchupScore: 0,
  };

  // Calculate synergies with current team
  teamPicks.forEach((ally) => {
    if (!ally || ally.name === hero.name) return;
    const score = getSynergyScore(hero.name, ally.name, synergyData);
    result.currentTeamSynergy += score;
    if (score > 0) {
      result.synergies.push({
        hero: ally,
        score,
        reason: getSynergyReason(hero.name, ally.name),
      });
    }
  });

  // Calculate matchups against enemy
  enemyPicks.forEach((enemy) => {
    if (!enemy) return;
    const score = getCounterScore(hero.name, enemy.name, counterData);
    result.currentMatchupScore += score;
    
    if (score > 20) {
      result.counters.push({
        hero: enemy,
        score,
        reason: getCounterReason(hero.name, enemy.name, true),
      });
    } else if (score < -20) {
      result.counteredBy.push({
        hero: enemy,
        score: Math.abs(score),
        reason: getCounterReason(hero.name, enemy.name, false),
      });
    }
  });

  // Sort by score
  result.synergies.sort((a, b) => b.score - a.score);
  result.counters.sort((a, b) => b.score - a.score);
  result.counteredBy.sort((a, b) => b.score - a.score);

  return result;
}

function getSynergyScore(hero1, hero2, synergyData) {
  if (synergyData?.[hero1]?.[hero2]) return synergyData[hero1][hero2];
  if (synergyData?.[hero2]?.[hero1]) return synergyData[hero2][hero1];
  // Placeholder: random score for demo
  return Math.floor(Math.random() * 40) + 30;
}

function getCounterScore(hero1, hero2, counterData) {
  if (counterData?.[hero1]?.[hero2]) return counterData[hero1][hero2];
  // Placeholder: random score for demo
  return Math.floor(Math.random() * 80) - 40;
}

function getSynergyReason(hero1, hero2) {
  const reasons = [
    `${hero1} provides setup for ${hero2}'s combo`,
    `Strong CC chain potential`,
    `Complementary damage types`,
    `Lane pressure synergy`,
    `Team fight wombo combo`,
    `Roaming duo potential`,
    `Split push coordination`,
  ];
  return reasons[Math.floor(Math.random() * reasons.length)];
}

function getCounterReason(hero1, hero2, isCounter) {
  if (isCounter) {
    const reasons = [
      `${hero1}'s kit directly counters ${hero2}`,
      `Mobility advantage in lane`,
      `Can interrupt key abilities`,
      `Sustain/poke advantage`,
      `Better scaling matchup`,
    ];
    return reasons[Math.floor(Math.random() * reasons.length)];
  }
  const reasons = [
    `${hero2} can burst before ${hero1} reacts`,
    `Kit disadvantage in fights`,
    `Weak against ${hero2}'s CC`,
    `Outscaled in late game`,
  ];
  return reasons[Math.floor(Math.random() * reasons.length)];
}

// ===== Get Best Synergies/Counters for hero pool =====
export function getHeroRecommendations({
  hero,
  availableHeroes = [],
  synergyData = null,
  counterData = null,
  topK = 5,
}) {
  const synergies = [];
  const counters = [];
  const counteredBy = [];

  availableHeroes.forEach((h) => {
    if (h.name === hero.name) return;

    const synergyScore = getSynergyScore(hero.name, h.name, synergyData);
    synergies.push({ hero: h, score: synergyScore });

    const counterScore = getCounterScore(hero.name, h.name, counterData);
    if (counterScore > 0) {
      counters.push({ hero: h, score: counterScore });
    } else {
      counteredBy.push({ hero: h, score: Math.abs(counterScore) });
    }
  });

  return {
    bestSynergies: synergies.sort((a, b) => b.score - a.score).slice(0, topK),
    bestCounters: counters.sort((a, b) => b.score - a.score).slice(0, topK),
    hardCounteredBy: counteredBy.sort((a, b) => b.score - a.score).slice(0, topK),
  };
}

// ===== Main Tooltip Component =====
export default function SynergyCounterTooltip({
  hero,
  teamPicks = [],
  enemyPicks = [],
  allHeroes = [],
  synergyData = null,
  counterData = null,
  position = { x: 0, y: 0 },
  onClose,
  getImage,
}) {
  const [activeTab, setActiveTab] = useState('current'); // current | pool

  const analysis = useMemo(() => {
    return analyzeHeroRelations({
      hero,
      teamPicks,
      enemyPicks,
      synergyData,
      counterData,
      allHeroes,
    });
  }, [hero, teamPicks, enemyPicks, synergyData, counterData, allHeroes]);

  const recommendations = useMemo(() => {
    return getHeroRecommendations({
      hero,
      availableHeroes: allHeroes,
      synergyData,
      counterData,
      topK: 5,
    });
  }, [hero, allHeroes, synergyData, counterData]);

  if (!hero) return null;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9, y: 10 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.9, y: 10 }}
      style={{
        position: 'fixed',
        left: Math.min(position.x + 20, window.innerWidth - 420),
        top: Math.min(position.y - 20, window.innerHeight - 500),
        width: 400,
        zIndex: 10000,
        pointerEvents: 'auto',
      }}
    >
      <div style={{
        background: 'linear-gradient(145deg, #0d0d14 0%, #1a1a28 100%)',
        borderRadius: 20,
        border: '1px solid rgba(255,255,255,0.1)',
        boxShadow: `
          0 20px 60px rgba(0,0,0,0.6),
          0 0 40px rgba(255,16,38,0.1),
          inset 0 1px 0 rgba(255,255,255,0.05)
        `,
        overflow: 'hidden',
      }}>
        {/* Header */}
        <TooltipHeader hero={hero} getImage={getImage} onClose={onClose} />

        {/* Synergy Score Ring */}
        <SynergyScoreRing 
          teamSynergy={analysis.currentTeamSynergy}
          matchupScore={analysis.currentMatchupScore}
        />

        {/* Tab Navigation */}
        <div style={{
          display: 'flex',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
        }}>
          <TabButton 
            active={activeTab === 'current'}
            onClick={() => setActiveTab('current')}
          >
            📊 Current Draft
          </TabButton>
          <TabButton 
            active={activeTab === 'pool'}
            onClick={() => setActiveTab('pool')}
          >
            🎯 Hero Pool
          </TabButton>
        </div>

        {/* Content */}
        <div style={{ padding: 16 }}>
          {activeTab === 'current' ? (
            <CurrentDraftAnalysis 
              analysis={analysis}
              getImage={getImage}
            />
          ) : (
            <HeroPoolAnalysis 
              recommendations={recommendations}
              getImage={getImage}
            />
          )}
        </div>
      </div>
    </motion.div>
  );
}

// ===== Header =====
function TooltipHeader({ hero, getImage, onClose }) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      padding: 16,
      background: 'linear-gradient(90deg, rgba(255,16,38,0.15), transparent)',
      borderBottom: '1px solid rgba(255,255,255,0.08)',
    }}>
      {/* Hero Image */}
      <div style={{
        position: 'relative',
        width: 64,
        height: 64,
        borderRadius: 16,
        overflow: 'hidden',
        border: '2px solid rgba(255,16,38,0.4)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
      }}>
        <img 
          src={getImage?.(hero.image) || '/placeholder.png'}
          alt={hero.name}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
        {/* Role Badge */}
        <div style={{
          position: 'absolute',
          bottom: -2,
          right: -2,
          padding: '2px 6px',
          borderRadius: 6,
          background: 'rgba(0,0,0,0.8)',
          fontSize: 10,
          color: '#fff',
          fontWeight: 700,
        }}>
          {hero.role}
        </div>
      </div>

      {/* Hero Info */}
      <div style={{ marginLeft: 16, flex: 1 }}>
        <h3 style={{
          margin: 0,
          fontSize: 20,
          fontWeight: 900,
          color: '#fff',
          letterSpacing: 0.5,
          fontFamily: "'Rajdhani', sans-serif",
        }}>
          {hero.name}
        </h3>
        <div style={{
          fontSize: 11,
          color: 'rgba(255,255,255,0.5)',
          marginTop: 4,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}>
          <span>🔗 Synergy & Counter Analysis</span>
        </div>
      </div>

      {/* Close Button */}
      <button
        onClick={onClose}
        style={{
          width: 32,
          height: 32,
          borderRadius: 8,
          border: 'none',
          background: 'rgba(255,255,255,0.05)',
          color: 'rgba(255,255,255,0.5)',
          cursor: 'pointer',
          fontSize: 18,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        ×
      </button>
    </div>
  );
}

// ===== Synergy Score Ring =====
function SynergyScoreRing({ teamSynergy, matchupScore }) {
  const totalScore = teamSynergy + matchupScore;
  const normalizedScore = Math.max(0, Math.min(100, 50 + totalScore));
  
  const getScoreColor = (score) => {
    if (score >= 70) return '#f5f5f5';
    if (score >= 50) return '#ffd93d';
    return '#ff6b6b';
  };

  const circumference = 2 * Math.PI * 45;
  const strokeDashoffset = circumference - (normalizedScore / 100) * circumference;

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      padding: '20px 16px',
      background: 'rgba(0,0,0,0.2)',
    }}>
      <div style={{ position: 'relative', width: 120, height: 120 }}>
        {/* Background Circle */}
        <svg width="120" height="120" style={{ transform: 'rotate(-90deg)' }}>
          <circle
            cx="60"
            cy="60"
            r="45"
            fill="none"
            stroke="rgba(255,255,255,0.1)"
            strokeWidth="8"
          />
          <motion.circle
            cx="60"
            cy="60"
            r="45"
            fill="none"
            stroke={getScoreColor(normalizedScore)}
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={circumference}
            initial={{ strokeDashoffset: circumference }}
            animate={{ strokeDashoffset }}
            transition={{ duration: 1, ease: 'easeOut' }}
            style={{
              filter: `drop-shadow(0 0 10px ${getScoreColor(normalizedScore)})`,
            }}
          />
        </svg>

        {/* Center Content */}
        <div style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <motion.span
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            style={{
              fontSize: 28,
              fontWeight: 900,
              color: getScoreColor(normalizedScore),
              fontFamily: "'Orbitron', sans-serif",
            }}
          >
            {normalizedScore.toFixed(0)}
          </motion.span>
          <span style={{
            fontSize: 10,
            color: 'rgba(255,255,255,0.5)',
            fontWeight: 700,
            letterSpacing: 1,
          }}>
            SYNERGY
          </span>
        </div>
      </div>

      {/* Score Breakdown */}
      <div style={{
        marginLeft: 24,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: 12,
      }}>
        <ScoreItem 
          label="Team Synergy"
          value={teamSynergy}
          icon="🔗"
          color="#f5f5f5"
        />
        <ScoreItem 
          label="Matchup Score"
          value={matchupScore}
          icon="⚔️"
          color="#ff6b6b"
        />
      </div>
    </div>
  );
}

function ScoreItem({ label, value, icon, color }) {
  const isPositive = value >= 0;
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 10,
    }}>
      <span style={{ fontSize: 16 }}>{icon}</span>
      <div>
        <div style={{
          fontSize: 10,
          color: 'rgba(255,255,255,0.5)',
          fontWeight: 700,
          letterSpacing: 0.5,
        }}>
          {label}
        </div>
        <div style={{
          fontSize: 16,
          fontWeight: 900,
          color: isPositive ? color : '#ff6b6b',
          fontFamily: "'JetBrains Mono', monospace",
        }}>
          {isPositive ? '+' : ''}{value.toFixed(0)}
        </div>
      </div>
    </div>
  );
}

// ===== Tab Button =====
function TabButton({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      style={{
        flex: 1,
        padding: '12px 16px',
        background: active ? 'rgba(255,16,38,0.15)' : 'transparent',
        border: 'none',
        borderBottom: active ? '2px solid #ff1026' : '2px solid transparent',
        color: active ? '#fff' : 'rgba(255,255,255,0.5)',
        fontSize: 12,
        fontWeight: 800,
        cursor: 'pointer',
        transition: 'all 0.2s',
      }}
    >
      {children}
    </button>
  );
}

// ===== Current Draft Analysis =====
function CurrentDraftAnalysis({ analysis, getImage }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Synergies with team */}
      <RelationSection
        title="✅ Synergies with Team"
        items={analysis.synergies}
        emptyText="No allies picked yet"
        type="synergy"
        getImage={getImage}
      />

      {/* Counters enemy */}
      <RelationSection
        title="⚔️ Counters"
        items={analysis.counters}
        emptyText="No clear counters"
        type="counter"
        getImage={getImage}
      />

      {/* Countered by */}
      <RelationSection
        title="❌ Countered By"
        items={analysis.counteredBy}
        emptyText="No hard counters"
        type="counteredBy"
        getImage={getImage}
      />
    </div>
  );
}

// ===== Hero Pool Analysis =====
function HeroPoolAnalysis({ recommendations, getImage }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <RelationSection
        title="🔗 Best Synergy Partners"
        items={recommendations.bestSynergies}
        emptyText="No data available"
        type="synergy"
        getImage={getImage}
      />

      <RelationSection
        title="⚔️ Can Counter"
        items={recommendations.bestCounters}
        emptyText="No data available"
        type="counter"
        getImage={getImage}
      />

      <RelationSection
        title="⚠️ Avoid Against"
        items={recommendations.hardCounteredBy}
        emptyText="No hard counters"
        type="counteredBy"
        getImage={getImage}
      />
    </div>
  );
}

// ===== Relation Section =====
function RelationSection({ title, items, emptyText, type, getImage }) {
  const colors = {
    synergy: { bg: 'rgba(245,245,245,0.1)', border: 'rgba(245,245,245,0.3)', text: '#f5f5f5' },
    counter: { bg: 'rgba(255,217,61,0.1)', border: 'rgba(255,217,61,0.3)', text: '#ffd93d' },
    counteredBy: { bg: 'rgba(255,107,107,0.1)', border: 'rgba(255,107,107,0.3)', text: '#ff6b6b' },
  };

  const color = colors[type];

  return (
    <div>
      <h4 style={{
        margin: '0 0 10px',
        fontSize: 12,
        fontWeight: 800,
        color: 'rgba(255,255,255,0.7)',
        letterSpacing: 0.5,
      }}>
        {title}
      </h4>

      {items.length === 0 ? (
        <div style={{
          padding: 16,
          borderRadius: 12,
          background: 'rgba(255,255,255,0.03)',
          textAlign: 'center',
          color: 'rgba(255,255,255,0.3)',
          fontSize: 12,
        }}>
          {emptyText}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {items.map((item, i) => (
            <motion.div
              key={item.hero?.name || i}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.05 }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '10px 14px',
                borderRadius: 12,
                background: color.bg,
                border: `1px solid ${color.border}`,
              }}
            >
              {/* Hero Image */}
              <div style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                overflow: 'hidden',
                border: `2px solid ${color.border}`,
                flexShrink: 0,
              }}>
                <img 
                  src={getImage?.(item.hero?.image) || '/placeholder.png'}
                  alt={item.hero?.name}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </div>

              {/* Info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: 13,
                  fontWeight: 800,
                  color: '#fff',
                }}>
                  {item.hero?.name || 'Unknown'}
                </div>
                {item.reason && (
                  <div style={{
                    fontSize: 10,
                    color: 'rgba(255,255,255,0.5)',
                    marginTop: 2,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}>
                    {item.reason}
                  </div>
                )}
              </div>

              {/* Score */}
              <div style={{
                fontSize: 14,
                fontWeight: 900,
                color: color.text,
                fontFamily: "'JetBrains Mono', monospace",
              }}>
                {type === 'counteredBy' ? '-' : '+'}{item.score?.toFixed(0) || 0}
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}

// ===== Inline Synergy Display (for hero grid) =====
export function InlineSynergyBadge({ 
  hero, 
  teamPicks = [], 
  synergyData = null,
}) {
  const synergyScore = useMemo(() => {
    let total = 0;
    teamPicks.forEach((ally) => {
      if (!ally || ally.name === hero.name) return;
      total += getSynergyScore(hero.name, ally.name, synergyData);
    });
    return total;
  }, [hero, teamPicks, synergyData]);

  if (teamPicks.length === 0 || synergyScore === 0) return null;

  const isGood = synergyScore > 30;
  const color = isGood ? '#f5f5f5' : synergyScore > 0 ? '#ffd93d' : '#ff6b6b';

  return (
    <motion.div
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      style={{
        position: 'absolute',
        top: 4,
        left: 4,
        padding: '2px 6px',
        borderRadius: 6,
        background: `${color}22`,
        border: `1px solid ${color}44`,
        fontSize: 9,
        fontWeight: 900,
        color: color,
        display: 'flex',
        alignItems: 'center',
        gap: 3,
      }}
    >
      <span>🔗</span>
      <span>{synergyScore > 0 ? '+' : ''}{synergyScore.toFixed(0)}</span>
    </motion.div>
  );
}

// ===== Connection Lines Component (for visual draft board) =====
export function SynergyLines({ 
  picks, 
  synergyData,
  containerRef,
  getHeroPosition, // (heroName) => { x, y }
}) {
  const [lines, setLines] = useState([]);

  useEffect(() => {
    const newLines = [];
    const teamPicks = picks.A || [];
    
    for (let i = 0; i < teamPicks.length; i++) {
      for (let j = i + 1; j < teamPicks.length; j++) {
        const hero1 = teamPicks[i];
        const hero2 = teamPicks[j];
        if (!hero1 || !hero2) continue;

        const score = getSynergyScore(hero1.name, hero2.name, synergyData);
        if (score > 20) {
          const pos1 = getHeroPosition?.(hero1.name);
          const pos2 = getHeroPosition?.(hero2.name);
          if (pos1 && pos2) {
            newLines.push({ from: pos1, to: pos2, score });
          }
        }
      }
    }
    setLines(newLines);
  }, [picks, synergyData, getHeroPosition]);

  return (
    <svg 
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 5,
      }}
    >
      {lines.map((line, i) => (
        <motion.line
          key={i}
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 0.5 }}
          x1={line.from.x}
          y1={line.from.y}
          x2={line.to.x}
          y2={line.to.y}
          stroke="#f5f5f5"
          strokeWidth={2}
          strokeDasharray="4 4"
        />
      ))}
    </svg>
  );
}
